import type { ShipClassId } from './ids';

/**
 * Combat (GDD §8): automatic, in rounds, between hostile fleets within a
 * ship's range (#117) — its own hex for planetary guns. The triangle corvette → cruiser → frigate → corvette makes the
 * enemy's fleet composition worth scouting. Starting values for tuning.
 */
export interface ShipCombatDef {
  readonly hull: number;
  /** Damage per round against a neutral target. */
  readonly damage: number;
  /** Classes it deals extra damage to and prefers as targets. */
  readonly strongAgainst: readonly ShipClassId[];
  /**
   * How far it fires, hexes (#117, the author's decision of 22.10.2026): 0 is
   * its own hex only. Every counter outranges its prey, except the corvette,
   * which is fast enough to close on the cruisers it hunts.
   */
  readonly range: number;
}

export const SHIP_COMBAT: Readonly<Record<ShipClassId, ShipCombatDef>> = {
  explorer: { hull: 50, damage: 0, strongAgainst: [], range: 0 },
  transport: { hull: 100, damage: 0, strongAgainst: [], range: 0 },
  // Fast torpedo boats: interceptors of cruisers and transports.
  corvette: {
    hull: 150,
    damage: 10,
    strongAgainst: ['cruiser', 'transport', 'tradeLiner', 'lander'],
    range: 1,
  },
  // Line ships with point defence against small craft.
  frigate: { hull: 300, damage: 12, strongAgainst: ['corvette', 'raider'], range: 2 },
  // Heavy guns: break frigates and planetary defences.
  cruiser: { hull: 550, damage: 22, strongAgainst: ['frigate'], range: 3 },
  // Armoured enough to reach orbit under fire, but unarmed.
  lander: { hull: 200, damage: 0, strongAgainst: [], range: 0 },
  tradeLiner: { hull: 125, damage: 0, strongAgainst: [], range: 0 },
  // Reforged raider: a corvette that hunts cargo.
  // Boards its prey: it has to come alongside.
  raider: {
    hull: 175,
    damage: 11,
    strongAgainst: ['transport', 'tradeLiner', 'cruiser'],
    range: 1,
  },
};

/**
 * A fleet of several ships, once «Логистика флота» is learnt (#160, the author's
 * decision of 28.10.2026). Any classes go together.
 */
export const FORMATION = {
  /** Ships in one fleet, at most. */
  maxShips: 4,
  /** Drill: damage share added for each armed ship beyond the first... */
  drillPerShip: 0.1,
  /** ...up to this much. */
  drillMax: 0.2,
} as const;

export const STANCE_IDS = ['aggressive', 'defensive', 'evasive'] as const;
export type StanceId = (typeof STANCE_IDS)[number];

export const COMBAT = {
  /** One combat round every this many ticks (1 s). */
  roundTicks: 10,
  /** Damage against a class the attacker is strong against... */
  strongMultiplier: 1.5,
  /** ...and from a ship whose target is strong against it. */
  weakMultiplier: 0.67,
  /** Damage dealt and taken by stance (GDD §8). Evasive fleets keep moving through a fight. */
  stances: {
    aggressive: { dealt: 1.2, taken: 1 },
    defensive: { dealt: 0.8, taken: 0.8 },
    evasive: { dealt: 0.5, taken: 1 },
  },
  /** A fleet retreats home once its hull falls below this share (GDD §8: at 50% losses). */
  defaultRetreatAt: 0.5,
  /** Metals left as debris per alloy of a destroyed ship's cost. */
  debrisPerAlloy: 1,
  /** Share of max hull repaired each cycle at the owner's capital. */
  repairPerCycle: 0.25,
} as const;
